import { useFormContext } from 'react-hook-form';

import { useCurrentUserContext } from 'utils/User';
import useFleetAdminPermissions from 'utils/User/useFleetAdminPermissions';

import { useCurrentUserManagedTeamsQuery } from './useCurrentUserManagedTeamsQuery';

interface UseCreateAccountCapabilityResult {
  /**
   * The OEM segment the form's current selections resolve to, or
   * undefined when the user hasn't picked one yet.
   */
  selectedSegmentId?: string;

  /**
   * `null` when no segment is selected yet — the form should stay
   * enabled in that case so the user can pick one.
   */
  capability: FleetAdminCapability | null;
}

/**
 * Resolve the Fleet Admin "canCreateAccount" capability for whatever
 * OEM segment the CreateAccountModal form currently points at:
 *
 *  - Superusers pick the segment directly via the `oemSegmentId` field.
 *  - Other OEM managers only pick a team; the segment is derived from
 *    that team's `oemSegment.id`.
 *
 * Returns `null` capability when nothing is selected so callers can
 * leave the form enabled until the user makes a choice.
 */
export default function useCreateAccountCapability(): UseCreateAccountCapabilityResult {
  const { watch } = useFormContext();
  const { isSuperUser } = useCurrentUserContext();
  const fleetAdminPermissions = useFleetAdminPermissions();

  const {
    data: {
      currentUserProfile: { user: { managedSentinelTeams = [] } = {} } = {},
    } = {},
  } = useCurrentUserManagedTeamsQuery();

  const oemSegmentValue = watch('oemSegmentId');
  const sentinelTeamIdValue = watch('sentinelTeamId');

  const selectedSegmentId = isSuperUser
    ? oemSegmentValue || undefined
    : managedSentinelTeams.find((team) => team.id === sentinelTeamIdValue)
        ?.oemSegment?.id;

  if (!selectedSegmentId) {
    return { selectedSegmentId: undefined, capability: null };
  }

  return {
    selectedSegmentId,
    capability: fleetAdminPermissions.capability(
      selectedSegmentId,
      'canCreateAccount',
    ),
  };
}
